import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import BottomNav from "@/components/feature/BottomNav";
import { appApi, coachApi } from "@/api";
import type { HomeServiceItem } from "@/api";
import { resolveCustomerAssetUrl } from "@/lib/asset";
import { auth } from "@/lib/auth";
import { locationStore } from "@/lib/location";
import { buildLoginRedirect } from "@/lib/navigation";
import { RequestError } from "@/lib/request";
import { storage, storageKeys } from "@/lib/storage";
import type { CoachListItem } from "@/types/api";

interface BookingServiceItem {
  id: number;
  name: string;
  duration: string;
  price: number;
  originalPrice: number;
  tag: string;
  image: string;
}

interface BookingTherapistItem {
  id: number;
  name: string;
  title: string;
  status: string;
  statusColor: string;
  rating: string;
  orders: number;
  operationTags: string[];
  specialty: string[];
  avatar: string;
}

interface StoredCustomerCity {
  id?: number | string | null;
}

function getStoredCityId() {
  const storedCity = storage.get<StoredCustomerCity>(storageKeys.city);
  const cityId = Number(storedCity?.id || 0);
  return Number.isFinite(cityId) && cityId > 0 ? cityId : undefined;
}

function isBookableCoach(item: CoachListItem) {
  return Number(item.serverStatus || 0) === 1 && Number(item.workStatus || 0) === 1;
}

function getCoachStatus(item: CoachListItem) {
  if (Number(item.serverStatus || 0) !== 1) {
    return { text: "暂停服务", color: "#909399" };
  }
  if (Number(item.workStatus || 0) === 1) {
    return { text: "可服务", color: "#17B978" };
  }
  return { text: "服务中", color: "#FF7B22" };
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function buildOrderConfirmUrl(goodsId: number, coachId: number, qty: number) {
  const params = new URLSearchParams({
    goodsId: String(goodsId),
    coachId: String(coachId),
    qty: String(qty),
  });
  return `/order-confirm?${params.toString()}`;
}

function getFirstServiceTag(item: HomeServiceItem) {
  if (Array.isArray(item.marketing_tags)) {
    const firstMarketingTag = item.marketing_tags
      .map((tag) => String(tag || "").trim())
      .find(Boolean);
    if (firstMarketingTag) {
      return firstMarketingTag;
    }
  }

  if (Array.isArray(item.tags)) {
    const firstTag = item.tags
      .map((tag) => String(tag || "").trim())
      .find(Boolean);
    if (firstTag) {
      return firstTag;
    }
  }

  if (typeof item.tags === "string") {
    return item.tags
      .split(/[|,，、]/)
      .map((tag) => tag.trim())
      .find(Boolean) || "";
  }

  return "";
}

function mapServiceItem(item: HomeServiceItem): BookingServiceItem {
  return {
    id: item.id,
    name: String(item.name || "服务项目"),
    duration: item.duration ? `${item.duration}分钟` : "时长待补充",
    price: Number(item.price || 0),
    originalPrice: Number(item.scribing_price || item.price || 0),
    tag: getFirstServiceTag(item),
    image:
      resolveCustomerAssetUrl(item.image) ||
      "https://readdy.ai/api/search-image?query=massage%20spa%20service%20warm%20light&width=120&height=120&seq=booking-service&orientation=squarish",
  };
}

function mapTherapistItem(item: CoachListItem): BookingTherapistItem {
  const specialty = Array.isArray(item.specialties) && item.specialties.length
    ? item.specialties
    : ["平台认证", "可上门服务"];
  const operationTags = (Array.isArray(item.operationTags)
    ? item.operationTags
    : String(item.operationTags || "").split(/[|,，、\s]+/))
    .map((tag) => String(tag || "").trim())
    .filter(Boolean)
    .slice(0, 2);
  const status = getCoachStatus(item);
  return {
    id: item.id,
    name: String(item.name || "技师"),
    title: String(item.level || "平台技师"),
    status: status.text,
    statusColor: status.color,
    rating: Number(item.rating || 0).toFixed(1),
    orders: Number(item.orderNum || 0),
    operationTags,
    specialty,
    avatar:
      resolveCustomerAssetUrl(String(item.workPhoto || "")) ||
      "https://readdy.ai/api/search-image?query=professional%20massage%20therapist%20portrait&width=96&height=96&seq=booking-coach&orientation=squarish",
  };
}

function prioritizePreferredService(items: BookingServiceItem[], preferredGoodsId: number) {
  if (!preferredGoodsId) {
    return items;
  }
  const preferredIndex = items.findIndex((item) => item.id === preferredGoodsId);
  if (preferredIndex <= 0) {
    return items;
  }
  const preferred = items[preferredIndex];
  return [preferred, ...items.slice(0, preferredIndex), ...items.slice(preferredIndex + 1)];
}

export default function BookingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const preferredGoodsId = Number(searchParams.get("goodsId") || 0);
  const preferredCoachId = Number(searchParams.get("coachId") || 0);

  const [services, setServices] = useState<BookingServiceItem[]>([]);
  const [therapists, setTherapists] = useState<BookingTherapistItem[]>([]);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [selectedTherapist, setSelectedTherapist] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        const currentLocation = locationStore.getFresh();
        const selectedCityId = getStoredCityId();
        const [serviceResult, coachResult] = await Promise.all([
          appApi.getHomeServiceList({ page_no: 1, page_size: preferredGoodsId > 0 ? 100 : 20 }),
          coachApi.getCoachList({
            pageNum: 1,
            pageSize: 20,
            cityId: selectedCityId,
            longitude: currentLocation?.longitude,
            latitude: currentLocation?.latitude,
            sortBy: "distance_asc",
          }),
        ]);

        if (cancelled) {
          return;
        }

        const nextServices = prioritizePreferredService(
          (serviceResult.lists || []).map(mapServiceItem),
          preferredGoodsId,
        );
        const nextTherapists = (coachResult.records || [])
          .filter(isBookableCoach)
          .map(mapTherapistItem);

        setServices(nextServices);
        setTherapists(nextTherapists);

        if (preferredGoodsId > 0) {
          setQuantities((current) => current[preferredGoodsId] ? current : { [preferredGoodsId]: 1 });
        }
        if (preferredCoachId > 0 && nextTherapists.some((item) => item.id === preferredCoachId)) {
          setSelectedTherapist((current) => current ?? preferredCoachId);
        }
      } catch (requestError) {
        if (cancelled) {
          return;
        }
        if (requestError instanceof RequestError && requestError.code === 1001) {
          navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
          return;
        }
        setError(getErrorMessage(requestError, "预约数据加载失败，请稍后重试"));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadData();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate, preferredCoachId, preferredGoodsId]);

  const getQty = (id: number) => quantities[id] ?? 0;

  const setQty = (id: number, value: number) => {
    setQuantities((prev) => {
      if (value <= 0) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { [id]: Math.min(value, 99) };
    });
  };

  const selectedEntries = useMemo(
    () => Object.entries(quantities)
      .map(([goodsId, qty]) => ({ goodsId: Number(goodsId), qty }))
      .filter((item) => item.qty > 0),
    [quantities],
  );

  const totalCount = selectedEntries.reduce((sum, item) => sum + item.qty, 0);
  const totalPrice = services.reduce((sum, item) => sum + item.price * (quantities[item.id] ?? 0), 0);
  const canSubmit = totalCount > 0;

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    if (!selectedTherapist) {
      setError("请选择技师");
      return;
    }
    if (selectedEntries.length > 1) {
      setError("当前仅支持单个服务下单，请保留一个服务项目");
      return;
    }

    const selectedItem = selectedEntries[0];
    const target = buildOrderConfirmUrl(selectedItem.goodsId, selectedTherapist, selectedItem.qty);
    if (!auth.getToken()) {
      navigate(buildLoginRedirect(target));
      return;
    }
    navigate(target);
  };

  return (
    <div
      className="flex flex-col min-h-screen pb-32"
      style={{ background: "#F7F7F7", maxWidth: 480, margin: "0 auto" }}
    >
      <div
        className="px-4 pt-10 pb-3 bg-white flex items-center gap-3 sticky top-0 z-40"
        style={{ borderBottom: "1px solid #F2F2F2" }}
      >
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-xl" style={{ color: "#1a1a1a" }} />
        </button>
        <h1 className="text-base font-bold" style={{ color: "#1a1a1a" }}>预约服务</h1>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pt-4 space-y-3">
        {error ? (
          <div
            className="rounded-2xl px-4 py-3 text-sm"
            style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}
          >
            {error}
          </div>
        ) : null}

        <section className="bg-white rounded-2xl p-4" style={{ border: "1px solid #F2F2F2" }}>
          <h3 className="text-sm font-bold mb-3" style={{ color: "#1a1a1a" }}>选择服务</h3>
          <div className="space-y-2">
            {loading ? (
              <div className="py-8 text-center text-sm" style={{ color: "#AAA" }}>加载中...</div>
            ) : services.length === 0 ? (
              <div className="py-8 text-center text-sm" style={{ color: "#AAA" }}>暂无可预约服务</div>
            ) : services.map((svc) => {
              const qty = getQty(svc.id);
              const selected = qty > 0;
              return (
                <div
                  key={svc.id}
                  className="flex items-center gap-3 p-3 rounded-xl transition-all"
                  style={{
                    background: selected ? "#FFF5F5" : "#FAFAFA",
                    border: `1.5px solid ${selected ? "#D4434F" : "#F0F0F0"}`,
                  }}
                >
                  <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                    <img src={svc.image} alt={svc.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold" style={{ color: "#1a1a1a" }}>{svc.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                      <p className="text-xs truncate min-w-0" style={{ color: "#aaa" }}>{svc.duration}</p>
                      {svc.tag ? (
                        <p className="text-xs truncate min-w-0" style={{ color: "#aaa" }}>· {svc.tag}</p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm font-bold" style={{ color: "#D4434F" }}>¥{svc.price}</span>
                      <span className="text-xs line-through" style={{ color: "#ccc" }}>¥{svc.originalPrice}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {qty > 0 ? (
                      <>
                        <button
                          onClick={() => setQty(svc.id, qty - 1)}
                          className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer transition-all"
                          style={{ background: "#F5F5F5", border: "1px solid #E8E8E8" }}
                        >
                          <i className="ri-subtract-line text-sm" style={{ color: "#888" }} />
                        </button>
                        <span className="text-sm font-bold w-4 text-center" style={{ color: "#1a1a1a" }}>{qty}</span>
                      </>
                    ) : null}
                    <button
                      onClick={() => {
                        setError("");
                        setQty(svc.id, qty + 1);
                      }}
                      className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer transition-all"
                      style={{ background: "linear-gradient(135deg, #FF8C3A, #D4434F)" }}
                    >
                      <i className="ri-add-line text-sm text-white" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-white rounded-2xl p-4" style={{ border: "1px solid #F2F2F2" }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold" style={{ color: "#1a1a1a" }}>选择技师</h3>
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ background: "#FFF0F0", color: "#D4434F", border: "1px solid #FFD6D6" }}
              >必选</span>
            </div>
            <button
              className="flex items-center gap-0.5 whitespace-nowrap text-xs font-medium opacity-0 pointer-events-none"
              style={{ color: "#D4434F" }}
            >
              查看全部
              <i className="ri-arrow-right-s-line" style={{ fontSize: 14 }} />
            </button>
          </div>

          <div className="space-y-2">
            {loading ? (
              <div className="py-8 text-center text-sm" style={{ color: "#AAA" }}>加载中...</div>
            ) : therapists.length === 0 ? (
              <div className="py-8 text-center text-sm" style={{ color: "#AAA" }}>暂无可预约技师</div>
            ) : therapists.map((item) => {
              const isSelected = selectedTherapist === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setError("");
                    setSelectedTherapist(item.id);
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all"
                  style={{
                    background: isSelected ? "#FFF5F5" : "#FAFAFA",
                    border: `1.5px solid ${isSelected ? "#D4434F" : "#F0F0F0"}`,
                  }}
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full overflow-hidden">
                      <img src={item.avatar} alt={item.name} className="w-full h-full object-cover object-top" />
                    </div>
                    {isSelected ? (
                      <div
                        className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ background: "#D4434F" }}
                      >
                        <i className="ri-check-line text-white" style={{ fontSize: 10 }} />
                      </div>
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold" style={{ color: "#1a1a1a" }}>{item.name}</span>
                      <span
                        className="text-xs rounded-full px-1.5 py-0.5 whitespace-nowrap font-medium"
                        style={{ background: "#FFF3EC", color: "#FF7B22", fontSize: 10 }}
                      >{item.title}</span>
                      <span
                        className="text-xs rounded-full px-1.5 py-0.5 whitespace-nowrap font-medium"
                        style={{ background: `${item.statusColor}14`, color: item.statusColor, fontSize: 10 }}
                      >{item.status}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="flex items-center gap-0.5">
                        <i className="ri-star-fill" style={{ color: "#FFAA00", fontSize: 11 }} />
                        <span className="text-xs font-medium" style={{ color: "#FF7B22" }}>{item.rating}</span>
                      </div>
                      <span className="text-xs" style={{ color: "#ccc" }}>·</span>
                      <span className="text-xs" style={{ color: "#aaa" }}>{item.orders}单</span>
                      {item.operationTags.map((tag) => (
                        <span key={`${item.id}-${tag}`} style={{ display: "contents" }}>
                          <span className="text-xs" style={{ color: "#ccc" }}>·</span>
                          <span className="text-xs" style={{ color: "#aaa" }}>{tag}</span>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {item.specialty.slice(0, 2).map((specialty) => (
                        <span
                          key={specialty}
                          className="text-xs rounded-full px-2 py-0.5 whitespace-nowrap"
                          style={{ background: "#F5F5F5", color: "#888", fontSize: 10 }}
                        >{specialty}</span>
                      ))}
                    </div>
                  </div>
                  {isSelected ? (
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl" style={{ color: "#D4434F" }} />
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        </section>
      </div>

      <div
        className="fixed z-50 px-4 py-3 bg-white"
        style={{
          bottom: 64,
          maxWidth: 480,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          borderTop: "1px solid #F0F0F0",
        }}
      >
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full py-3.5 rounded-full text-white font-bold text-sm cursor-pointer whitespace-nowrap transition-all"
          style={{
            background: canSubmit ? "linear-gradient(90deg, #FF8C3A 0%, #D4434F 100%)" : "#E0E0E0",
            letterSpacing: "0.04em",
          }}
        >
          {canSubmit ? `确认预约 · ¥${totalPrice}（共${totalCount}项）` : "请选择服务项目"}
        </button>
      </div>

      <BottomNav />
    </div>
  );
}
